import { useState, useEffect } from "react";
import { Loader2, Shield, ShieldCheck, ArrowRight, Copy, CheckCheck, RefreshCw, Clock, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/tenantClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Logo } from "@/components/common/Logo";

interface MFASetupFormProps {
  onComplete: () => void;
  onSkip: () => void;
}

export function MFASetupForm({ onComplete, onSkip }: MFASetupFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [secret, setSecret] = useState("");
  const [qrCodeSvg, setQrCodeSvg] = useState("");
  const [factorId, setFactorId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [qrExpiresAt, setQrExpiresAt] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);

  const QR_EXPIRY_SECONDS = 120;

  // Countdown timer
  useEffect(() => {
    if (!qrExpiresAt || !showSetup) {
      setTimeRemaining(0);
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((qrExpiresAt - now) / 1000));
      setTimeRemaining(remaining);

      if (remaining === 0 && factorId) {
        supabase.auth.mfa.unenroll({ factorId }).catch(console.error);
        setQrCodeSvg("");
        setSecret("");
        setFactorId("");
        toast({
          title: "QR code expired",
          description: "The QR code has expired. Please generate a new one.",
          variant: "destructive",
        });
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [qrExpiresAt, showSetup, factorId, toast]);

  const generateRecoveryCodes = (): string[] => {
    const codes: string[] = [];
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    
    for (let i = 0; i < 10; i++) {
      let code = "";
      for (let j = 0; j < 8; j++) {
        code += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      codes.push(`${code.slice(0, 4)}-${code.slice(4, 8)}`);
    }
    
    return codes;
  };

  const hashCode = async (code: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(code);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const startEnrollment = async () => {
    setLoading(true);
    try {
      // Clean up existing factors
      const { data: existingFactors } = await supabase.auth.mfa.listFactors();
      if (existingFactors?.totp) {
        for (const factor of existingFactors.totp) {
          await supabase.auth.mfa.unenroll({ factorId: factor.id }).catch(console.error);
        }
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'CNVRGNX',
        issuer: 'CNVRGNX',
      });

      if (error) throw error;

      if (data) {
        setFactorId(data.id);
        setSecret(data.totp.secret);
        setQrCodeSvg(data.totp.qr_code);
        setQrExpiresAt(Date.now() + QR_EXPIRY_SECONDS * 1000);
        setShowSetup(true);
      }
    } catch (error: any) {
      toast({
        title: "Error starting setup",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast({
        title: "Invalid code",
        description: "Please enter a 6-digit verification code",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: verificationCode,
      });

      if (error) throw error;

      // Generate and store recovery codes
      const newRecoveryCodes = generateRecoveryCodes();
      const hashedCodes = await Promise.all(
        newRecoveryCodes.map(async (code) => ({
          user_id: user!.id,
          code_hash: await hashCode(code),
        }))
      );

      await supabase.from("recovery_codes").insert(hashedCodes);

      // Log audit event
      await supabase.from("audit_logs").insert({
        user_id: user!.id,
        event_type: "2fa_enabled",
        event_category: "security",
        description: "Two-factor authentication was enabled",
      });

      toast({
        title: "2FA enabled successfully",
        description: "Your account is now protected with two-factor authentication",
      });

      setRecoveryCodes(newRecoveryCodes);
      setShowRecoveryCodes(true);
    } catch (error: any) {
      toast({
        title: "Verification failed",
        description: error.message || "Invalid verification code",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopiedSecret(true);
    toast({ title: "Secret copied" });
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  const downloadRecoveryCodes = () => {
    const content = `CNVRGNX Recovery Codes\n\nKeep these codes safe. Each code can only be used once.\n\n${recoveryCodes.join('\n')}\n\nGenerated: ${new Date().toISOString()}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cnvrgnx-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleContinue = () => {
    toast({
      title: "Welcome!",
      description: "Successfully signed in.",
      duration: 5000,
    });
    onComplete();
  };

  const handleSkip = () => {
    toast({
      title: "Welcome!",
      description: "Successfully signed in.",
      duration: 5000,
    });
    onSkip();
  };

  // Show recovery codes after setup
  if (showRecoveryCodes) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center space-y-4">
          <Logo size="lg" />
          <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
            <ShieldCheck className="h-8 w-8 text-success" />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Save Your Recovery Codes</h1>
            <p className="text-muted-foreground mt-1">
              Store these codes safely. You'll need them if you lose access to your authenticator.
            </p>
          </div>
        </div>

        <Alert>
          <AlertDescription>
            Each code can only be used once. Download or copy them now.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm">
          {recoveryCodes.map((code, index) => (
            <div key={index} className="px-2 py-1 bg-background rounded">
              {code}
            </div>
          ))}
        </div>

        <Button onClick={downloadRecoveryCodes} variant="outline" className="w-full">
          <Download className="mr-2 h-4 w-4" />
          Download Codes
        </Button>

        <Button onClick={handleContinue} className="w-full">
          Continue to Dashboard
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    );
  }

  // Show setup form
  if (showSetup) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center space-y-4">
          <Logo size="lg" />
          <div className="text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Scan QR Code</h1>
            <p className="text-muted-foreground mt-1">
              Use your authenticator app to scan this code
            </p>
          </div>
        </div>

        {qrCodeSvg && timeRemaining > 0 ? (
          <>
            <div className="flex justify-center">
              <div 
                className="bg-white p-4 rounded-lg"
                dangerouslySetInnerHTML={{ __html: qrCodeSvg }}
              />
            </div>

            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Expires in {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
            </div>

            <Collapsible>
              <CollapsibleTrigger className="text-sm text-primary hover:underline w-full text-center">
                Can't scan? Enter code manually
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-4">
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-muted rounded text-sm font-mono break-all">
                    {secret}
                  </code>
                  <Button variant="outline" size="icon" onClick={copySecret}>
                    {copiedSecret ? <CheckCheck className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <div className="space-y-2">
              <Label>Verification code</Label>
              <Input
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
              />
            </div>

            <Button 
              onClick={verifyAndEnable} 
              disabled={verificationCode.length !== 6 || loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify & Enable 2FA"
              )}
            </Button>
          </>
        ) : (
          <div className="text-center space-y-4">
            <p className="text-muted-foreground">QR code expired</p>
            <Button onClick={startEnrollment} disabled={loading}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Generate New Code
            </Button>
          </div>
        )}

        <Button variant="ghost" onClick={handleSkip} className="w-full text-muted-foreground">
          Skip for now
        </Button>
      </div>
    );
  }

  // Initial prompt
  return (
    <div className="space-y-8">
      <div className="flex flex-col items-center space-y-4">
        <Logo size="lg" />
        <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Shield className="h-8 w-8 text-primary" />
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Secure Your Account</h1>
          <p className="text-muted-foreground mt-1">
            Add two-factor authentication for extra security
          </p>
        </div>
      </div>

      <div className="space-y-3 text-sm">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-success mt-0.5 shrink-0" />
          <span>Protects your account even if your password is compromised</span>
        </div>
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-success mt-0.5 shrink-0" />
          <span>Uses industry-standard TOTP authentication</span>
        </div>
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-success mt-0.5 shrink-0" />
          <span>Works with Google Authenticator, Authy, or 1Password</span>
        </div>
      </div>

      <div className="space-y-2">
        <Button onClick={startEnrollment} disabled={loading} className="w-full">
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Setting up...
            </>
          ) : (
            <>
              Set Up 2FA Now
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
        <Button variant="ghost" onClick={handleSkip} className="w-full text-muted-foreground">
          Skip for now
        </Button>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        You can enable 2FA later from your security settings
      </p>
    </div>
  );
}
